<template>
  <div class="webboard-page">
    <div class="webboard-header">
      <div>
        <div class="page-title mb-2">เว็บบอร์ด</div>
        <div class="theme-color-eight">รายการกระทู้</div>
      </div>

      <div class="webboard-header-actions">
        <div class="webboard-search">
          <TextField
            :value="keyword"
            hide-details
            placeholder="ค้นหา"
            append-icon="ic:baseline-search"
            @onChange="keyword = $event"
            @onEnter="onSearch"
          />
        </div>

        <Button
          class="create-topic-button"
          min-width="150px"
          @onClick="onCreateTopic"
        >
          <div class="d-flex align-center gap-8">
            <Iconify icon="iconamoon:comment-add" size="18px" color="#FFFFFF" />
            <span>ตั้งกระทู้ใหม่</span>
          </div>
        </Button>
      </div>
    </div>

    <div class="topic-list mt-8">
      <div
        v-for="topic in filteredTopics"
        :key="topic.id"
        class="topic-card"
        @click="openTopic(topic.id)"
      >
        <div class="topic-card-main">
          <div class="topic-avatar">{{ topic.authorInitial }}</div>

          <div class="topic-content">
            <div class="topic-meta">
              <div class="topic-author">{{ topic.author }}</div>
              <div class="topic-date">{{ topic.dateText }}</div>
            </div>

            <div class="topic-title">
              {{ topic.title }}
            </div>
          </div>
        </div>

        <div class="topic-stats">
          <div class="stat-pill">
            <Iconify icon="mdi:eye-outline" size="14px" color="#FFFFFF" />
            <span>{{ topic.view_count }}</span>
          </div>

          <div class="stat-pill">
            <Iconify icon="mdi:comment-processing-outline" size="14px" color="#FFFFFF" />
            <span>{{ topic.comments_count }}</span>
          </div>
        </div>
      </div>

      <div v-if="filteredTopics.length === 0" class="empty-state">
        ไม่พบกระทู้ที่ต้องการค้นหา
      </div>
    </div>

    <div class="d-flex justify-center mt-8 mb-4">
      <v-pagination
        v-model="paginate.current_page"
        :length="paginate.last_page"
        color="#DE7033"
        :total-visible="7"
        @input="getTopics"
      />
    </div>
  </div>
</template>

<script>
import Button from "@/components/input/Button.vue";
import TextField from "@/components/input/TextField.vue";

export default {
  name: "WebboardIndex",
  components: {
    Button,
    TextField,
  },
  data() {
    return {
      keyword: "",
      topics: [],
      paginate: {
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1,
      },
    };
  },
  created() {
    this.getTopics();
  },
  computed: {
    filteredTopics() {
      return this.topics;
    },
  },
  methods: {
    async getTopics(page) {
      const res = await this.$apiFire("Webboard/index", {
        keyword: this.keyword,
        page: page || this.paginate.current_page,
        per_page: this.paginate.per_page,
      });

      if (!res || !res.data) return;

      const topicResponse = res.data || {};

      this.topics = (topicResponse.data || []).map((topic) => ({
        id: topic.id,
        title: topic.title,
        author:
          topic?.user?.name ||
          [topic?.user?.firstname, topic?.user?.lastname].filter(Boolean).join(" ") ||
          "-",
        authorInitial: (topic?.user?.name || topic?.user?.firstname || "W")
          .toString()
          .charAt(0)
          .toUpperCase(),
        dateText: this.$moment(topic.created_at).add(543, "year").format("DD MMMM YYYY"),
        view_count: topic.view_count || 0,
        comments_count: topic.comments_count || 0,
      }));

      this.paginate = {
        total: topicResponse.total || 0,
        per_page: topicResponse.per_page || 10,
        current_page: topicResponse.current_page || 1,
        last_page: topicResponse.last_page || 1,
      };
    },
    onSearch() {
      this.paginate.current_page = 1;
      this.getTopics(1);
    },
    onCreateTopic() {
      this.$router.push({ name: "WebboardCreate" });
    },
    openTopic(id) {
      this.$router.push({ name: "WebboardDetail", params: { id } });
    },
  },
};
</script>

<style scoped lang="scss">
.webboard-page {
  min-height: calc(100vh - var(--header-height));
}

.webboard-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
}

.webboard-header-actions {
  width: 100%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

.webboard-search {
  width: 100%;
}

.create-topic-button {
  border-radius: 30px !important;
  background: #f15a29 !important;
  box-shadow: 1px 1px 10px 0 rgba(0, 0, 0, 0.25) !important;
}

.topic-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.topic-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(70, 70, 71, 0.08);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.topic-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(70, 70, 71, 0.12);
}

.topic-card-main {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
  flex: 1;
}

.topic-avatar {
  width: 28px;
  min-width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(180deg, #ff8a4c 0%, #f15a29 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-family: var(--font-bold-type);
}

.topic-content {
  min-width: 0;
  flex: 1;
}

.topic-meta {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
}

.topic-author {
  font-size: 12px;
  font-family: var(--font-bold-type);
  color: var(--theme-color-two);
}

.topic-date {
  font-size: 11px;
  color: var(--theme-color-eight);
}

.topic-title {
  font-size: 16px;
  color: var(--theme-color-two);
  line-height: 1.5;
}

.topic-stats {
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
}

.stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffb241 0%, #f7941d 100%);
  color: #fff;
  font-size: 12px;
  font-family: var(--font-bold-type);
  box-shadow: 0 6px 14px rgba(241, 90, 41, 0.18);
}

.empty-state {
  padding: 32px 20px;
  background: #fff;
  border-radius: 12px;
  text-align: center;
  color: var(--theme-color-eight);
}

@media (max-width: 960px) {
  .webboard-header {
    flex-direction: column;
  }

  .webboard-header-actions {
    max-width: none;
    align-items: stretch;
  }

  .topic-card {
    flex-direction: column;
    align-items: stretch;
  }

  .topic-stats {
    justify-content: flex-end;
  }
}
</style>
